/**
Author - Brijesh Pandey
Git - https://bitbucket.org/__brijesh/
**/

import React, {useEffect, useState, useContext, useRef} from 'react';
import styled from 'styled-components';
import env from 'env';

import {getKycStatus, skipPartnerSubFlow, partialResetFlow} from 'api';
import {
  KycScreenMapping,
  kycScreenDisplayNameMapping,
  kycScreenDescriptionMapping,
  kycScreenDisplayNameOverridesAllFi,
  kycScreenDescriptionOverridesAllFi,
} from 'types';
import {handleApiError} from 'helpers';
import {
  FLOW_ACC_NAME_MAPPING,
  FLOW_DISPLAY_NAME,
  KYC_FLOWS,
  KYC_PROGRESS_PERCENTAGE,
  KYC_PROGRESS_PERCENTAGE_PRE_KYC,
  PARTNER_FLOWS,
} from 'constants/FlowConstants';

import {ClientAppCommunicationContext} from 'context/CommunicationsContext';
import {getSource} from 'utils/ClientAppUtils';
import {resolveKycUserId, setAnalyticsUserId} from 'utils/analyticsUserId';
import {useWhiteLabelDocumentMeta} from 'hooks/useWhiteLabelDocumentMeta';

import {
  BankDetails,
  CheckDigiFlow,
  NomineeDetails,
  InitiateApiCalls,
  PersonalDetails,
  SuccessScreen,
  FaceMatch,
  FnoDeclarations,
  PartnerKycSuccessScreen,
  PartnerBankDetails,
  CheckArn,
  DocumentVerification,
  PartnerAgreement,
  PartnerGst,
  BankProof,
  Optional,
  Address,
  AddressProof,
  OpsEvaluation,
  Passport,
  TradingPreferences,
  ArnCardUpload,
  EuinCardUpload,
  Choice,
  UploadIdsContainer,
  FATCAForm,
  AccountAggregator,
  MinorDetails,
  RelationshipProof,
} from 'screens';

import {ThemeProvider} from '../styles/ThemeContext';

import {Error} from 'screens/commons';
import RPD from 'screens/RPD';
import ResponsivePageLayout from 'components/common/ResponsivePageLayout';
import GlobalStyles from 'static/GlobalStyles';
import {ProgressBar} from 'components/ProgressBar';

// NOTE: `wlId` is no longer used for white-label theme resolution here (the
// theme now comes inline on the status response, keyed by the authenticated
// user's own user_id server-side) - kept as a prop only because App.js still
// forwards it for an unrelated redirect-URL use.

const getScreenTextMappings = (flow) => {
  const isAllFi = flow === KYC_FLOWS.ALL_FI;
  return {
    displayNameMapping: isAllFi
      ? {...kycScreenDisplayNameMapping, ...kycScreenDisplayNameOverridesAllFi}
      : kycScreenDisplayNameMapping,
    descriptionMapping: isAllFi
      ? {...kycScreenDescriptionMapping, ...kycScreenDescriptionOverridesAllFi}
      : kycScreenDescriptionMapping,
  };
};

const RouterContainer = () => {
  const [kycData, setKycData] = useState({
    status: 'incomplete',
    stage: '',
    flow: 'mf',
    journeyData: {},
    // hasSubFlow: true,
    // subFlow: {
    //   ctaName: "Activate F&O",
    //   redirectUrl: "https://kyc.wealthydev.in/redirect?token=Bearer eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJzdWIiOiJreWNfMDg2MGM1MjZjODkyOWMiLCJleHAiOjE2ODU2NDE0Njl9.8b3r8cJCi0cR2-klrfpbFGwfrEcJwgiSC-uI6xFsOIE"
    // }
    // prefill: {
    //   "beneficiaryAccountIfsc": "BARB0CITYLT",
    //   // "beneficiaryAccountNo": "123456789012",
    //   "name": "S N Singh",
    //   "dob": "1954-08-01",
    //   "relation": "Father",
    //   "isMinor": true,
    //   "guardianDob": "1990-01-01",
    //   "guardianName": "Brijesh"
    // }
  });

  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);
  const [skipStep, setSkipStep] = useState(0);
  const [progressPercentage, setProgressPercentage] = useState(0);
  const [steps, setSteps] = useState([]);
  const clientAppCommsContext = useContext(ClientAppCommunicationContext);
  const scrollRef = useRef(null);

  const isPartnerFlow = PARTNER_FLOWS.includes(kycData.flow);
  const [whiteLabelConfig, setWhiteLabelConfig] = useState(null);
  useWhiteLabelDocumentMeta(whiteLabelConfig);

  const {displayNameMapping: screenDisplayNameMapping} = getScreenTextMappings(kycData.flow);

  useEffect(() => {
    updateStatus();
  }, []);

  useEffect(() => {
    window.sessionStorage.setItem('stage', kycData.stage);
    if (
      window.sessionStorage.getItem('kycId') === 'undefined' ||
      window.sessionStorage.getItem('kycId') === null
    ) {
      window.sessionStorage.setItem('kycId', kycData.kycId);
    } else if (window.sessionStorage.getItem('kycId') !== kycData.kycId) {
      window.sessionStorage.setItem('kycId', kycData.kycId);
    }
  }, [kycData]);

  useEffect(() => {
    if (clientAppCommsContext && clientAppCommsContext.subscribeToEvents) {
      clientAppCommsContext?.subscribeToEvents(handleClose);
    }
  }, [clientAppCommsContext]);

  const scrollIntoView = () => {
    if (!scrollRef?.current) return;
    scrollRef?.current?.scrollIntoView({behavior: 'smooth'});
  };

  const updateStatus = async () => {
    try {
      setIsLoading(true);

      const response = await getKycStatus();
      const result = response.data;

      // White-label theme now comes back inline on the status response
      // (keyed off the KYC applicant's own user_id server-side) instead of
      // a separate sequential wl_id-based call. Only set it when present so
      // a transient cache-miss/failure on a later poll doesn't blow away an
      // already-resolved theme (no frontend caching layer - relies solely
      // on HydraGoServices' 24h Redis cache on the backend).
      if (result?.theme) {
        setWhiteLabelConfig(result.theme);
      }

      setKycData({
        ...kycData,
        ...result,
      });

      calculateProgress(result);

      if (result?.stage === 'COMPLETE') {
        clientAppCommsContext.sendEventsToClientApp('REFRESH');
      }
      scrollIntoView();
      setAnalyticsUserId(resolveKycUserId(result));
    } catch (error) {
      handleApiError(error);
      setIsError(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSkipStep = (step) => {
    setSkipStep(step);
  };

  const handleClose = () => {
    const redirectUrl = window.sessionStorage.getItem('redirectTo');
    const source = getSource();

    //TODO: Find a proper fix for mobile web view back nav
    if (redirectUrl.includes('/client/profile/edit')) {
      window.location.assign(`${env.VALKYRIE_URL}/edit?source=${source}`);
    } else if (redirectUrl.includes('/client/profile')) {
      window.location.assign(`${env.VALKYRIE_URL}?source=${source}`);
    } else clientAppCommsContext.sendEventsToClientApp('CLOSE');
  };

  const skipPartnerStep = async () => {
    try {
      setIsLoading(true);
      await skipPartnerSubFlow();
      await updateStatus();
    } catch (error) {
      handleApiError(error);
      setIsError(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleErrorState = (value) => {
    setIsError(value);
  };

  const handleGoBack = async (reasons) => {
    try {
      const data = {
        kyc_id: kycData.id,
        rejections: {
          reasons: reasons,
        },
      };
      await partialResetFlow(data);
      updateStatus();
    } catch (error) {
      setIsError(error);
    }
  };

  /* eslint-disable prettier/prettier */
  const partnerKycComponentMapping = {
    DIGILOCKER: <InitiateApiCalls kycData={kycData} updateStatus={updateStatus} />,
    SELFIE: <InitiateApiCalls kycData={kycData} updateStatus={updateStatus} />,
    PAN_ANALYSIS: <InitiateApiCalls kycData={kycData} updateStatus={updateStatus} />,
    CHOICE: <CheckDigiFlow kycData={kycData} updateStatus={updateStatus} />,
    UPLOAD_PAN: <InitiateApiCalls handleErrorState={handleErrorState} kycData={kycData} updateStatus={updateStatus} />,
    CHECK_ARN: <CheckArn kycData={kycData} updateStatus={updateStatus} skipPartnerStep={skipPartnerStep} checkArn={true} />,
    META_FORM: <DocumentVerification kycData={kycData} updateStatus={updateStatus} skipPartnerStep={skipPartnerStep} />,
    AGREEMENT: <PartnerAgreement kycData={kycData} updateStatus={updateStatus} skipPartnerStep={skipPartnerStep} />,
    GST_DETAILS: <PartnerGst kycData={kycData} updateStatus={updateStatus} skipPartnerStep={skipPartnerStep} />,
    PENNY_DROP: <PartnerBankDetails kycData={kycData} updateStatus={updateStatus} skipPartnerStep={skipPartnerStep} />,
    BANK_PROOF: <BankProof updateStatus={updateStatus} handleGoBack={handleGoBack} />,
    COMPLETE: <PartnerKycSuccessScreen kycData={kycData} updateStatus={updateStatus} skipPartnerStep={skipPartnerStep} />,
    ADDRESS: <Address kycData={kycData} updateStatus={updateStatus} />,
    ADDRESS_PROOF: <AddressProof updateStatus={updateStatus} />,
    OPS_EVALUATION: <OpsEvaluation />,
    PASSPORT: <Passport updateStatus={updateStatus} />,
    TRADING_PREFERENCES: <TradingPreferences updateStatus={updateStatus} />,
    UPLOAD_ARN: <ArnCardUpload updateStatus={updateStatus} />,
    UPLOAD_EUIN: <EuinCardUpload updateStatus={updateStatus} />,
    ARN_CHOICE: <CheckArn kycData={kycData} updateStatus={updateStatus} skipPartnerStep={skipPartnerStep} checkArn={false} />,
    RPD: <RPD updateStatus={updateStatus} />,
  };
  /* eslint-enable prettier/prettier */

  const kycComponentMapping = {
    DIGILOCKER: <InitiateApiCalls kycData={kycData} updateStatus={updateStatus} />,
    SELFIE: <InitiateApiCalls kycData={kycData} updateStatus={updateStatus} />,
    E_SIGN: <InitiateApiCalls kycData={kycData} updateStatus={updateStatus} />,
    DDPI: <InitiateApiCalls kycData={kycData} updateStatus={updateStatus} />,
    SPECIMEN_SIGN: <InitiateApiCalls kycData={kycData} updateStatus={updateStatus} />,
    FACE_MATCH: <FaceMatch kycData={kycData} updateStatus={updateStatus} />,
    UPLOAD_PAN: <InitiateApiCalls kycData={kycData} updateStatus={updateStatus} />,
    NOMINEE: <NomineeDetails kycData={kycData} updateStatus={updateStatus} />,
    PENNY_DROP: <BankDetails kycData={kycData} updateStatus={updateStatus} />,
    PERSONAL_DETAILS: <PersonalDetails updateStatus={updateStatus} kycData={kycData} />,
    INCOME_DECLARATION: <FnoDeclarations updateStatus={updateStatus} />,
    COMPLETE: <SuccessScreen kycData={kycData} updateStatus={updateStatus} />,
    BANK_PROOF: (
      <BankProof updateStatus={updateStatus} handleGoBack={handleGoBack} kycData={kycData} />
    ),
    ADDRESS: <Address kycData={kycData} updateStatus={updateStatus} />,
    IDS_CHOICE: <Choice kycData={kycData} updateStatus={updateStatus} />,
    UPLOAD_IDS: <UploadIdsContainer updateStatus={updateStatus} />,
    ADDRESS_PROOF: <AddressProof updateStatus={updateStatus} />,
    OPS_EVALUATION: <OpsEvaluation />,
    PASSPORT: <Passport updateStatus={updateStatus} />,
    TRADING_PREFERENCES: <TradingPreferences kycData={kycData} updateStatus={updateStatus} />,
    FATCA: <FATCAForm updateStatus={updateStatus} />,
    RPD: <RPD kycData={kycData} updateStatus={updateStatus} />,
    AA: <AccountAggregator updateStatus={updateStatus} flow={kycData.flow} />,
    MINOR_DETAILS: <MinorDetails updateStatus={updateStatus} kycData={kycData} />,
    RELATIONSHIP_PROOF: <RelationshipProof updateStatus={updateStatus} />,
  };

  const get_flow_heading = () => {
    let formattedName = '';
    if (kycData.fullName) {
      formattedName = kycData.fullName
        .toLowerCase()
        .split(' ')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
    }
    const arr = [formattedName, FLOW_ACC_NAME_MAPPING[kycData.flow]];
    return arr.filter(Boolean).join(' | ');
  };

  const calculateProgress = (kycData) => {
    let newProgressPercentage = 0; //TODO: update percentage based on the stage and add pre-percentage
    let newSteps = [];

    if (!Array.isArray(kycData?.journeyData?.steps)) {
      kycData.journeyData = kycData.journeyData || {};
      kycData.journeyData.steps = [];
      //FIXME: remove this once the backend is updated
    }

    if (kycData.journeyData && kycData.journeyData.steps && kycData.journeyData.projectedPath) {
      const completedSteps = kycData.journeyData.steps.filter(
        (step) => step.stepStatus === 'COMPLETE'
      ).length;
      const totalSteps = kycData.journeyData.projectedPath.length;

      // Both pre kyc and kyc steps are included in the progress percentage
      newProgressPercentage =
        Math.round((completedSteps / totalSteps) * KYC_PROGRESS_PERCENTAGE[kycData.flow]) +
        KYC_PROGRESS_PERCENTAGE_PRE_KYC[kycData.flow];

      const {displayNameMapping, descriptionMapping} = getScreenTextMappings(kycData.flow);

      newSteps = kycData.journeyData.projectedPath.map((pathStep) => {
        const stepInfo = kycData.journeyData.steps.find(
          (step) => step.stepName.toUpperCase() === pathStep.stepName.toUpperCase()
        );

        const isCurrent =
          kycData.journeyData.current &&
          pathStep.stepName.toUpperCase() === kycData.stage.toUpperCase();

        return {
          id: pathStep.stepName,
          label: displayNameMapping[pathStep.stepName.toUpperCase()] || '',
          current: isCurrent,
          status: stepInfo?.stepStatus,
          description: descriptionMapping[pathStep.stepName.toUpperCase()] || '',
        };
      });
    }

    setProgressPercentage(newProgressPercentage);
    setSteps(newSteps);
  };

  const renderBasedOnFlow = () => {
    const componentMapping = isPartnerFlow ? partnerKycComponentMapping : kycComponentMapping;

    let component = <></>;
    if (kycData.isOptional && skipStep === 0) {
      component = (
        <Optional
          updateStatus={updateStatus}
          flow={kycData.flow}
          handleSkipStep={handleSkipStep}
          skipStep={skipStep}
        />
      );
    } else {
      switch (kycData.stage) {
        case KycScreenMapping[kycData.stage]:
          component = componentMapping[KycScreenMapping[kycData.stage]];
          break;
        default:
          component = <InitiateApiCalls kycData={kycData} updateStatus={updateStatus} />;
          break;
      }
    }

    if (isLoading)
      return (
        <ProgressLoaderContainer>
          <ProgressBar
            subheading="Please wait while we are fetching your kyc details"
            heading="Processing to next step"
            waitTimeInSec={30}
            stopOnEndTime={true}
            stopOnEndMsg={'Something went wrong. Please try again...'}
            showShadow={true}
          />
        </ProgressLoaderContainer>
      );

    return (
      <ResponsivePageLayout
        title={screenDisplayNameMapping[kycData.stage]}
        subtitle={FLOW_DISPLAY_NAME[kycData.flow] ? get_flow_heading() : ''}
        progressPercentage={progressPercentage}
        steps={steps}
        onClose={handleClose}
        sideBarTitle={`${FLOW_DISPLAY_NAME[kycData.flow] ?? ''} KYC`}
        hideBackButton={true}
        crn={kycData?.crn}
      >
        <ComponentsWrapper>
          {isError ? (
            <ErrorContainer>
              <Error />
            </ErrorContainer>
          ) : (
            component
          )}
        </ComponentsWrapper>
      </ResponsivePageLayout>
    );
  };

  return (
    <ThemeProvider
      initialTheme={isPartnerFlow ? 'partner' : 'client'}
      whiteLabelConfig={whiteLabelConfig}
    >
      <GlobalStyles />
      {renderBasedOnFlow()}
    </ThemeProvider>
  );
};

const ComponentsWrapper = styled.div`
  width: 100%;
  height: 100%;
  overflow: auto;

  border: 1px solid ${(props) => props.theme.colors.border?.default || '#DDDDDD'};
  border-radius: ${(props) => props.theme.borderRadius?.md || '8px'};
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  background: ${(props) => props.theme.colors.background.primary};

  @media (max-width: 768px) {
    border: none;
    box-shadow: none;
  }

  .custom-ion-page {
    overflow: auto;
  }
`;

const ErrorContainer = styled.div`
  background: #ffffff;
  height: 100%;
  width: 100%;
  scroll-behavior: scroll;
  position: relative;
  overflow-y: scroll;
  text-align: center;
  margin: 0;

  .error-image {
    width: 10rem;
  }

  @media (max-width: 768px) {
    background: white;
    .error-image {
      width: 8rem;
    }
  }
`;

const ProgressLoaderContainer = styled.div`
  width: 100%;
  height: 100vh;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: #f8f9fa;
`;

export default RouterContainer;
